/*
 * What the "Run on a case" picker offers before anyone types: the cases open
 * in the tab bar, most recently used first, with those matching the skill's
 * visas lifted to the top. No React or IO so the order is unit-testable.
 */
import type { AppTab } from "@/features/appTabs/types";
import type { CaseRef } from "@/features/caseIde/routes";

import type { FirmSkill } from "@/features/firmKnowledge/types";

export type SuggestedCase = {
  ref: CaseRef;
  label: string;
  sublabel?: string;
  /** The case's visa is one the skill is filtered to. */
  matches: boolean;
};

const CASE_PREFIX = "case:";

/** The visas the skill is for, then those its automations are filtered to, deduped. */
export const skillVisas = (
  skill: Pick<FirmSkill, "triggers" | "visas">
): string[] => [
  ...new Set([
    ...(skill.visas ?? []),
    ...skill.triggers.flatMap((t) => t.case_filter?.visa_types ?? [])
  ])
];

export const visaMatches = (
  workflow: string | null | undefined,
  visas: string[]
): boolean => {
  const text = (workflow ?? "").toLowerCase();
  return (
    !!text && visas.some((v) => v && text.includes(v.trim().toLowerCase()))
  );
};

export const openCaseSuggestions = (
  tabs: AppTab[],
  recency: string[],
  visas: string[],
  limit = 6
): SuggestedCase[] => {
  const rank = (id: string) => {
    const i = recency.indexOf(id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return tabs
    .filter((t) => t.kind === "case" && t.id.startsWith(CASE_PREFIX))
    .map((t) => ({
      tab: t,
      matches: visaMatches(t.workflow, visas),
      rank: rank(t.id)
    }))
    .sort((a, b) => Number(b.matches) - Number(a.matches) || a.rank - b.rank)
    .slice(0, limit)
    .map(({ tab, matches }) => ({
      ref: {
        individualId: tab.id.slice(CASE_PREFIX.length),
        foreignNationalId: tab.clientId ?? null
      },
      label: tab.title,
      sublabel: tab.workflow,
      matches
    }));
};
